import { Link } from "react-router-dom";

const GRANTS = [
  { name: "Community Seed Grant", range: "₦80,000 – ₦200,000", tag: "Entry Level", desc: "For early-stage initiatives and grassroots projects demonstrating strong community need and a clear execution plan.", eligibility: ["Registered NGO or community group", "Operating for 6+ months", "Clear project budget", "Local community endorsement"] },
  { name: "Startup Growth Grant", range: "₦2,000,000 – ₦5,000,000", tag: "Startup", desc: "For innovative startups and business initiatives in agriculture, technology, or trade seeking capital to scale.", eligibility: ["Registered business or individual startup plan", "Clear business model", "Growth potential", "Identified market need"] },
  { name: "Development Acceleration Grant", range: "₦5,000,000 – ₦20,000,000", tag: "Mid-Scale", desc: "For established organizations seeking to expand their reach, hire staff, or scale a proven program to new communities.", eligibility: ["Registered NGO with 2+ years", "Track record of impact", "Audited financials", "Board of directors"] },
  { name: "Impact Partnership Grant", range: "₦10,000,000 – ₦50,000,000", tag: "Large Scale", desc: "For high-impact programs with national reach potential. Includes mentorship, capacity building, and monitoring support.", eligibility: ["Minimum 5 years operation", "Multi-state presence", "Previous grant experience", "M&E framework in place"] },
  { name: "Emergency Relief Grant", range: "₦50,000 – ₦250,000", tag: "Rapid Response", desc: "Fast-tracked grants for communities facing natural disasters, health crises, or urgent humanitarian needs.", eligibility: ["Documented emergency", "Any registered organization", "Fast-track 72hr review", "Immediate deployment plan"] },
];

const CATEGORIES = ["Education & Literacy","Youth Empowerment","Women & Girls","Health & Sanitation","Agriculture & Food Security","Fishery","Poultry","Poverty Alleviation","Community Infrastructure","Environmental Sustainability","Business Development","Climate & Environment"];

const PROCESS = [
  { step: "01", title: "Become a Member", desc: "Register and pay the ₦250 one-time membership fee to unlock access to all grant programs." },
  { step: "02", title: "Choose Your Grant", desc: "Review the available grant categories and identify the right fit for your project scale." },
  { step: "03", title: "Submit Application", desc: "Complete the online application form with project details, budget, and bank account information." },
  { step: "04", title: "Review & Evaluation", desc: "Our team reviews applications within 14–21 days against our equity and impact criteria." },
  { step: "05", title: "Decision & Notification", desc: "Applicants are notified of outcomes via email and member dashboard. Approved grants move to disbursement." },
  { step: "06", title: "Fund Disbursement", desc: "Approved funds are transferred directly to your registered bank account within 5–7 business days." },
];

export default function ProgramsPage() {
  return (
    <>
      <style>{`
        .prog-hero { background:linear-gradient(160deg,#071428,#0B1F3A,#122844); padding:140px 5vw 80px; text-align:center; }
        .prog-hero h1 { font-family:'Playfair Display',serif; font-size:clamp(36px,6vw,68px); font-weight:900; color:white; margin-bottom:18px; }
        .grants-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:28px; max-width:1200px; margin:0 auto; }
        .grant-card { border:1px solid var(--border-light); background:white; padding:36px; position:relative; transition:all 0.3s; }
        .grant-card:hover { transform:translateY(-3px); box-shadow:var(--shadow-md); border-color:rgba(201,150,62,0.2); }
        .grant-tag { display:inline-block; background:rgba(201,150,62,0.1); color:#C9963E; font-size:11px; font-weight:600; letter-spacing:1px; text-transform:uppercase; padding:4px 10px; border-radius:2px; margin-bottom:14px; }
        .grant-range { font-family:'Playfair Display',serif; font-size:20px; font-weight:700; color:#0B1F3A; margin-bottom:12px; }
        .elig-list li { font-size:13px; color:var(--text-body); padding:4px 0 4px 18px; position:relative; }
        .elig-list li::before { content:'✓'; position:absolute; left:0; color:#C9963E; font-weight:700; }
        .cats-wrap { display:flex; flex-wrap:wrap; gap:10px; max-width:900px; margin:32px auto 0; justify-content:center; }
        .cat-pill { padding:8px 18px; border:1px solid var(--border-light); border-radius:50px; font-size:13px; color:var(--text-body); background:white; transition:all 0.2s; cursor:pointer; }
        .cat-pill:hover { border-color:#C9963E; color:#C9963E; }
        .process-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:28px; max-width:1200px; margin:52px auto 0; }
        .process-card { padding:28px; border:1px solid var(--border-light); background:white; }
        .process-num { font-family:'Playfair Display',serif; font-size:44px; font-weight:900; color:rgba(201,150,62,0.15); line-height:1; margin-bottom:10px; }
        @media(max-width:900px){ .grants-grid{grid-template-columns:1fr;} .process-grid{grid-template-columns:repeat(2,1fr);} }
        @media(max-width:560px){ .process-grid{grid-template-columns:1fr;} }
      `}</style>

      <section className="prog-hero">
        <span className="section-eyebrow" style={{ color: "#E8B96A" }}>Programs & Grants</span>
        <h1>Funding That Meets<br /><em style={{ fontStyle: "italic", color: "#E8B96A" }}>You Where You Are</em></h1>
        <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 17, maxWidth: 560, margin: "0 auto", lineHeight: 1.75 }}>
          From seed-stage community initiatives to large-scale national programs — we have a grant pathway for every stage of your journey.
        </p>
      </section>

      {/* GRANTS */}
      <section style={{ padding: "90px 5vw", background: "var(--off-white)" }}>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <span className="section-eyebrow">Available Grants</span>
          <h2 className="section-title">Grant Programs</h2>
        </div>
        <div className="grants-grid">
          {GRANTS.map(g => (
            <div className="grant-card" key={g.name}>
              <div style={{ width: 40, height: 4, background: "var(--gold)", marginBottom: 20 }}></div>
              <div className="grant-tag">{g.tag}</div>
              <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 21, fontWeight: 700, color: "var(--navy)", marginBottom: 6 }}>{g.name}</h3>
              <div className="grant-range">{g.range}</div>
              <p style={{ fontSize: 14, color: "var(--text-muted)", lineHeight: 1.7, marginBottom: 18 }}>{g.desc}</p>
              <div style={{ fontSize: 11, color: "#C9963E", fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>Eligibility Requirements</div>
              <ul className="elig-list">
                {g.eligibility.map(e => <li key={e}>{e}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section style={{ padding: "70px 5vw", background: "white", textAlign: "center" }}>
        <span className="section-eyebrow">What We Fund</span>
        <h2 className="section-title">Grant Categories</h2>
        <p style={{ color: "var(--text-muted)", fontSize: 16, maxWidth: 560, margin: "0 auto" }}>Applications are accepted across these thematic areas.</p>
        <div className="cats-wrap">
          {CATEGORIES.map(c => <div className="cat-pill" key={c}>{c}</div>)}
        </div>
      </section>

      {/* PROCESS */}
      <section style={{ padding: "80px 5vw", background: "var(--off-white)" }}>
        <div style={{ textAlign: "center" }}>
          <span className="section-eyebrow">How It Works</span>
          <h2 className="section-title">Application Process</h2>
        </div>
        <div className="process-grid">
          {PROCESS.map(p => (
            <div className="process-card" key={p.step}>
              <div className="process-num">{p.step}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "var(--navy)", marginBottom: 8 }}>{p.title}</div>
              <div style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.7 }}>{p.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: "#0B1F3A", padding: "70px 5vw", textAlign: "center" }}>
        <span className="section-eyebrow" style={{ color: "#E8B96A" }}>Start Your Journey</span>
        <h2 className="section-title light" style={{ marginBottom: 16 }}>Ready to Apply?</h2>
        <p style={{ color: "rgba(255,255,255,0.55)", fontSize: 16, marginBottom: 32 }}>Members can apply immediately. Not yet a member? Join for just ₦250.</p>
        <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
          <Link to="/apply" className="btn-primary">Start Application →</Link>
          <Link to="/membership" className="btn-outline">Join as Member — ₦250</Link>
        </div>
      </section>
    </>
  );
}
